import React, { useEffect } from 'react';
import { Routes, Route, Link as RouterLink, useNavigate, Navigate } from 'react-router-dom';
import { ChakraProvider, Box, Container, Flex, Heading, Text, Button, SimpleGrid, Card, CardBody, Link, HStack, Menu, MenuButton, MenuList, MenuItem, Avatar, Badge, Spinner } from '@chakra-ui/react';
import { FiChevronDown, FiLogOut } from 'react-icons/fi';
import { useAuth0 } from '@auth0/auth0-react';
import theme from './theme';
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import PatientList from './components/PatientList';
import PatientForm from './components/PatientForm';
import PatientDetail from './components/PatientDetail';
import ServiceList from './components/ServiceList';
import ServiceForm from './components/ServiceForm';
import Settings from './components/Settings';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { setAccessTokenGetter } from './services/apiConfig';
import { isMultiTenantMode } from './utils/tenant';

function App() {
  return (
    <ChakraProvider theme={theme}>
      <AppWithAuth />
    </ChakraProvider>
  );
}

function AppWithAuth() {
  const { isLoading, getAccessTokenSilently } = useAuth0();

  // Configure API client to use Auth0 tokens
  useEffect(() => {
    setAccessTokenGetter(getAccessTokenSilently);
  }, [getAccessTokenSilently]);

  if (isLoading) {
    return (
      <Flex h="100vh" alignItems="center" justifyContent="center">
        <Spinner size="xl" color="purple.500" thickness="4px" />
      </Flex>
    );
  }

  return (
    <TenantProvider>
      <Routes>
        {/* Root path - Landing page or redirect to login for subdomains */}
        <Route path="/" element={<RootRedirect />} />

        {/* Login Page */}
        <Route path="/login" element={<Login />} />

        {/* Callback route for Auth0 */}
        <Route path="/callback" element={<CallbackPage />} />

        {/* EMR Application Routes - Protected */}
        <Route path="/app/*" element={
          <ProtectedRoute>
            <EMRApp />
          </ProtectedRoute>
        } />
      </Routes>
    </TenantProvider>
  );
}

// Component to handle root path - show landing page for root domain, redirect to login for subdomains
function RootRedirect() {
  const { isAuthenticated } = useAuth0();

  // If on a subdomain (tenant-specific URL), redirect to appropriate page
  if (isMultiTenantMode()) {
    // If already authenticated, go to app, otherwise go to login
    return <Navigate to={isAuthenticated ? "/app" : "/login"} replace />;
  }

  // On root domain, show the marketing landing page
  return <LandingPage />;
}

function CallbackPage() {
  const navigate = useNavigate();
  const { isAuthenticated, error, isLoading } = useAuth0();

  useEffect(() => {
    console.log('CallbackPage state:', { isAuthenticated, isLoading, error });

    if (isAuthenticated) {
      console.log('✅ Authenticated, redirecting to /app');
      navigate('/app');
    }
  }, [isAuthenticated, isLoading, error, navigate]);

  // Show error if authentication failed
  if (error) {
    return (
      <Flex h="100vh" alignItems="center" justifyContent="center">
        <Box textAlign="center" maxW="500px" p={8}>
          <Text fontSize="2xl" fontWeight="bold" color="red.500" mb={4}>
            Authentication Error
          </Text>
          <Text color="gray.600" mb={4}>
            {error.message}
          </Text>
          <Button colorScheme="purple" onClick={() => navigate('/login')}>
            Back to Login
          </Button>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex h="100vh" alignItems="center" justifyContent="center">
      <Box textAlign="center">
        <Spinner size="xl" color="purple.500" thickness="4px" mb={4} />
        <Text>Completing login...</Text>
        <Text fontSize="sm" color="gray.500" mt={2}>
          isAuthenticated: {isAuthenticated ? 'yes' : 'no'}, isLoading: {isLoading ? 'yes' : 'no'}
        </Text>
      </Box>
    </Flex>
  );
}

function EMRApp() {
  const { logout, user } = useAuth0();
  const { tenant, loading: tenantLoading } = useTenant();

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  };

  if (tenantLoading) {
    return (
      <Flex h="100vh" alignItems="center" justifyContent="center">
        <Spinner size="xl" color="purple.500" thickness="4px" />
      </Flex>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Navigation */}
      <Box
        as="nav"
        bg="white"
        boxShadow="sm"
        borderBottomWidth="1px"
        borderBottomColor="gray.200"
        position="sticky"
        top="0"
        zIndex="sticky"
        backdropFilter="blur(10px)"
        bgColor="whiteAlpha.900"
      >
        <Container maxW="container.xl">
          <Flex h="16" alignItems="center" justifyContent="space-between">
            <HStack spacing={3}>
              <Link
                as={RouterLink}
                to="/app"
                fontSize="xl"
                fontWeight="bold"
                bgGradient="linear(to-r, brand.darkPurple, brand.purple)"
                bgClip="text"
                _hover={{ textDecoration: 'none' }}
              >
                Radiancebook EMR
              </Link>
              {tenant && !tenant.isLocal && (
                <Badge colorScheme="purple" fontSize="sm" px={2} py={1} borderRadius="md">
                  {tenant.name}
                </Badge>
              )}
            </HStack>
            <HStack spacing={4}>
              <HStack spacing={1}>
                <Button as={RouterLink} to="/app/patients" variant="ghost" colorScheme="purple">
                  Patients
                </Button>
                <Button as={RouterLink} to="/app/settings" variant="ghost" colorScheme="purple">
                  Settings
                </Button>
              </HStack>

              {/* User Menu */}
              <Menu>
                <MenuButton
                  as={Button}
                  variant="ghost"
                  rightIcon={<FiChevronDown />}
                  leftIcon={<Avatar size="sm" name={user?.name || 'User'} bg="brand.purple" />}
                >
                  {user?.name || user?.email || 'User'}
                </MenuButton>
                <MenuList>
                  <MenuItem icon={<FiLogOut />} onClick={handleLogout}>
                    Logout
                  </MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxW="container.xl" py={8}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/patients" element={<PatientList />} />
          <Route path="/patients/new" element={<PatientForm />} />
          <Route path="/patients/:id" element={<PatientDetail />} />
          <Route path="/patients/edit/:id" element={<PatientForm />} />
          <Route path="/services" element={<ServiceList />} />
          <Route path="/services/new" element={<ServiceForm />} />
          <Route path="/services/edit/:id" element={<ServiceForm />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </Container>
    </Box>
  );
}

function Home() {
  return (
    <Box>
      <Heading
        as="h1"
        size="2xl"
        mb={4}
        bgGradient="linear(to-r, brand.darkPurple, brand.darkGray)"
        bgClip="text"
      >
        Welcome to Radiancebook EMR
      </Heading>
      <Text fontSize="lg" color="gray.600" mb={8}>
        A modern Electronic Medical Records system for medical aesthetics clinics.
      </Text>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
        <Card
          borderRadius="xl"
          overflow="hidden"
          transition="all 0.3s"
          _hover={{ transform: 'translateY(-4px)', boxShadow: 'lg' }}
        >
          <CardBody>
            <Heading size="md" mb={3} color="brand.darkPurple">
              Patients
            </Heading>
            <Text color="gray.600" mb={4}>
              Manage patient records, demographics, and medical history.
            </Text>
            <Button as={RouterLink} to="/app/patients" width="full" colorScheme="purple">
              View Patients
            </Button>
          </CardBody>
        </Card>

        <Card
          borderRadius="xl"
          overflow="hidden"
          transition="all 0.3s"
          _hover={{ transform: 'translateY(-4px)', boxShadow: 'lg' }}
        >
          <CardBody>
            <Heading size="md" mb={3} color="brand.darkPurple">
              Services
            </Heading>
            <Text color="gray.600" mb={4}>
              Manage treatment services, consultations, and pricing.
            </Text>
            <Button as={RouterLink} to="/app/services" width="full" colorScheme="purple">
              View Services
            </Button>
          </CardBody>
        </Card>

      </SimpleGrid>
    </Box>
  );
}

export default App;
